/** @format */

import React, { useState } from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Tooltip,
  Pie,
  Cell,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import insolData from "../../Data/insol_temp_Calc.json";
import shadowmask from "../../Data/shadowmask.json";
import sunpath from "../../Data/sunpath_new1.json";
import "./AnalysisChart.css";

const AnalysisCharts = () => {
  const [activeChart, setActiveChart] = useState("insolation");

  // Monthly Insolation Data
  const insolationData = [
    { month: "Jan", value: 150 },
    { month: "Feb", value: 165 },
    { month: "Mar", value: 185 },
    { month: "Apr", value: 195 },
    { month: "May", value: 175 },
    { month: "Jun", value: 160 },
  ];

  // 🔹 Chart 1: Insolation & Temperature (Monthly)
  const insolationTempData = {
    labels: Array.from({ length: 12 }, (_, i) => `Month ${i + 1}`),
    datasets: [
      {
        label: "Avg Insolation (kWh/m²)",
        data: insolData.insol_avg,
        borderColor: "orange",
        backgroundColor: "rgba(255,165,0,0.3)",
        fill: true,
        tension: 0.3,
      },
      {
        label: "Max Avg Temp (°C)",
        data: insolData.temp_max_avg_monthly,
        borderColor: "red",
        backgroundColor: "rgba(255,0,0,0.3)",
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const insolationTempOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Monthly Insolation & Temperature - 2025" },
    },
  };

  // 🔹 Chart 2: Shadowmask Intensity over Time
  const shadowmaskDataset = Object.values(shadowmask)[0]; // Taking the first set
  const shadowLabels = shadowmaskDataset.map((_, i) => `T${i + 1}`);

  const shadowMaskData = {
    labels: shadowLabels,
    datasets: [
      {
        label: "Shadowmask Intensity",
        data: shadowmaskDataset,
        borderColor: "gray",
        backgroundColor: "rgba(128,128,128,0.3)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const shadowMaskOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Shadowmask Time Series" },
    },
  };

  // 🔹 Chart 3: Sunpath Azimuth vs Altitude
  const sepPath = sunpath["sep"];
  const marPath = sunpath["mar"];

  const formatPath = (path, label, color) => ({
    label,
    data: path.map(([azimuth, altitude]) => ({ x: azimuth, y: altitude })),
    showLine: true,
    borderColor: color,
    backgroundColor: color,
    tension: 0.3,
  });

  const sunpathData = {
    datasets: [
      formatPath(sepPath, "September Path", "orange"),
      formatPath(marPath, "March Path", "blue"),
    ],
  };

  const sunpathOptions = {
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: { display: true, text: "Sun Path: Azimuth vs Altitude" },
    },
    scales: {
      x: { title: { display: true, text: "Azimuth (°)" } },
      y: { title: { display: true, text: "Altitude (°)" } },
    },
  };

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Legend
  );
  // Seasonal Distribution Data
  const seasonalData = [
    { name: "Jan-Jun", value: 75, color: "#ff6b9d" },
    { name: "Jul-Dec", value: 25, color: "#4ecdc4" },
  ];

  // Wind Speed Radar Data
  const windData = [
    { direction: "Jan", speed: 10 },
    { direction: "Apr", speed: 8 },
    { direction: "Jul", speed: 6 },
    { direction: "Oct", speed: 4 },
  ];

  return (
    <div className="container">
      <div className="analysis-charts">
        <div className="charts-header">
          <h2>Analysis Charts</h2>
          <div className="chart-selector">
            <button
              className={activeChart === "insolation" ? "active" : ""}
              onClick={() => setActiveChart("insolation")}>
              Insolation
            </button>
            <button
              className={activeChart === "seasonal" ? "active" : ""}
              onClick={() => setActiveChart("seasonal")}>
              Seasonal
            </button>
            <button
              className={activeChart === "wind" ? "active" : ""}
              onClick={() => setActiveChart("wind")}>
              Wind
            </button>
          </div>
        </div>
        <div className="charts-container">
          {activeChart === "insolation" && (
            <div className="chart-wrapper">
              <div className="chart-legend">
                <div className="legend-item">
                  <span
                    className="legend-color"
                    style={{ backgroundColor: "#4ecdc4" }}></span>
                  <span>Monthly Insolation (W/m²)</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={insolationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(255, 255, 255, 0.95)",
                      border: "none",
                      borderRadius: "8px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#4ecdc4"
                    strokeWidth={3}
                    fill="#4ecdc4"
                    fillOpacity={0.2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeChart === "seasonal" && (
            <div className="chart-wrapper">
              <div className="pie-chart-container">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={seasonalData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value">
                      {seasonalData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pie-legend">
                  {seasonalData.map((item, index) => (
                    <div key={index} className="legend-item">
                      <span
                        className="legend-color"
                        style={{ backgroundColor: item.color }}></span>
                      <span>{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeChart === "wind" && (
            <div className="chart-wrapper">
              <div className="wind-chart-container">
                <div className="wind-legend">
                  <div className="legend-item">
                    <span
                      className="legend-color"
                      style={{ backgroundColor: "#ff6b9d" }}></span>
                    <span>Wind Speed (km/h)</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <RadarChart data={windData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis
                      dataKey="direction"
                      tick={{ fill: "#64748b", fontSize: 10 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 10]}
                      tick={{ fill: "#64748b", fontSize: 10 }}
                    />
                    <Radar
                      name="Wind Speed"
                      dataKey="speed"
                      stroke="#ff6b9d"
                      fill="#ff6b9d"
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
        <br></br>
        <h3>Tamil Nadu Climate & Astro Map</h3>
        {/* TODO: Add map integration */}
        <p>Shadowmask Zones Loaded: {Object.keys(shadowmask).length}</p>
        <p>Sunpath Entries: {Object.keys(sunpath).length}</p>
        <br></br>
        <h2>Analysis Overview</h2>
        <Scatter data={sunpathData} options={sunpathOptions} />
        <Line data={insolationTempData} options={insolationTempOptions} />
        <Line data={shadowMaskData} options={shadowMaskOptions} />
        <br></br>
        <p>Shadowmask Points: {shadowmaskDataset.length}</p>
        <p>Insolation Avg: {insolData.insol_avg?.join(", ")}</p>
      </div>
    </div>
  );
};

export default AnalysisCharts;
